// account.js

import { sendAuthenticatedRequest, setupAuthentication } from './auth.js'; // Adjust the import path as needed
import { getUser, fetchCardData } from './data/update_operations.js'; // Adjust the import path as needed
import config from '../config/index.js'; // Adjust the import path as needed
import './navbar.js';

document.addEventListener('DOMContentLoaded', () => {
  // Initialize authentication
  setupAuthentication(handleAuthenticatedUser, handleUnauthenticatedUser);
});

async function setupProfilePic(token, serverside_user) {
  let cropper;
  let croppedBlob = null;
  let currentImageURL = null;   // object URL for the image currently in the cropper
  let previewImageURL = null;   // object URL for the post-crop preview
  const objectURLS = new Set(); // to revoke on cleanup

  const fileInput = document.getElementById('profile-picture-input');
  const modal = document.getElementById('cropper-modal');
  const cropperWrapper = document.getElementById('cropper-wrapper');
  const cropperImage = document.getElementById('cropper-image');
  const previewWrapper = document.getElementById('cropper-preview');
  const previewImg = document.getElementById('cropper-preview-img');
  const cropBtn = document.getElementById('crop-preview-btn');
  const saveBtn = document.getElementById('crop-save-btn');
  const cancelBtn = document.getElementById('crop-cancel-btn');
  const profilePicture = document.getElementById('profile-picture');
  const defaultIcon = document.getElementById('default-profile-icon');
  const updateCropButton = document.getElementById('update-crop-button');

  function revoke(url) {
    if (url) {
      try { URL.revokeObjectURL(url); } catch {}
      objectURLS.delete(url);
    }
  }

  function openFileSelector() {
    // reset input so selecting same file twice still fires 'change'
    fileInput.value = '';
    fileInput.click();
  }

  function openCropper(srcURL) {
    croppedBlob = null;
    previewWrapper.style.display = 'none';
    previewImg.style.display = 'none';
    cropperWrapper.style.display = 'block';
    cropBtn.style.display = 'inline-flex';
    saveBtn.style.display = 'none';
    modal.style.display = 'flex';

    // Tear down any existing cropper
    if (cropper) { cropper.destroy(); cropper = null; }

    cropperImage.onload = () => {
      cropper = new Cropper(cropperImage, {
        aspectRatio: 1,
        viewMode: 1,
        background: false,
        minContainerHeight: 300,
        minContainerWidth: 300,
        guides: false,
        center: false,
      });
    };

    cropperImage.onerror = () => {
      alert('Error loading image. Please try a different file.');
      // Clean up if the URL failed
      revoke(srcURL);
    };

    cropperImage.src = srcURL;
  }

  // 1) Load an existing profile picture (download → blob → object URL)
  if (serverside_user.profile_pic_id) {
    try {
      const downloadUrl = `${config.detectionUrl}/uploads/${serverside_user.profile_pic_id}.jpg`;
      const picRes = await fetch(downloadUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (picRes.ok) {
        const picBlob = await picRes.blob();
        const picURL = URL.createObjectURL(picBlob);
        objectURLS.add(picURL);
        profilePicture.src = picURL;
        defaultIcon.style.display = 'none';
        profilePicture.style.display = 'inline-block';
        updateCropButton.style.display = 'inline-flex';
      } else {
        console.error('Failed to download existing profile picture.');
      }
    } catch (e) {
      console.error('Error downloading existing profile picture:', e);
    }
  }

  defaultIcon.addEventListener('click', openFileSelector);
  profilePicture.addEventListener('click', openFileSelector);

  // Re-crop the currently shown picture (use whatever is displayed)
  updateCropButton.addEventListener('click', () => {
    if (!profilePicture.src) return;
    // If it's not a blob: URL (e.g., came from <img src="...">), fetch → blob → URL for safe canvas use
    if (!profilePicture.src.startsWith('blob:')) {
      fetch(profilePicture.src)
        .then(r => r.blob())
        .then(b => {
          const url = URL.createObjectURL(b);
          objectURLS.add(url);
          // keep track so we can revoke later
          revoke(currentImageURL);
          currentImageURL = url;
          openCropper(currentImageURL);
        })
        .catch(() => alert('Could not load existing image for cropping.'));
    } else {
      revoke(currentImageURL);
      currentImageURL = profilePicture.src;
      openCropper(currentImageURL);
    }
  });

  // **Windows/Mac-safe file loader**: use createObjectURL (not FileReader)
  fileInput.addEventListener('change', () => {
    const file = fileInput.files && fileInput.files[0];
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      alert('Please choose a valid image.');
      return;
    }

    // Free previous cropper image URL
    revoke(currentImageURL);

    const url = URL.createObjectURL(file);
    objectURLS.add(url);
    currentImageURL = url;
    openCropper(currentImageURL);
  });

  cropBtn.addEventListener('click', () => {
    if (!cropper) return;
    const canvas = cropper.getCroppedCanvas({ width: 300, height: 300 });

    canvas.toBlob((blob) => {
      if (!blob) {
        alert('Cropping failed. Try another image.');
        return;
      }
      croppedBlob = blob;

      // Clean old preview URL and create a new one
      revoke(previewImageURL);
      previewImageURL = URL.createObjectURL(blob);
      objectURLS.add(previewImageURL);

      profilePicture.src = previewImageURL;
      profilePicture.style.display = 'inline-block';
      defaultIcon.style.display = 'none';

      previewImg.src = previewImageURL;
      previewImg.style.display = 'inline-block';
      previewWrapper.style.display = 'block';
      cropperWrapper.style.display = 'none';
      cropBtn.style.display = 'none';
      saveBtn.style.display = 'inline-flex';
      updateCropButton.style.display = 'inline-flex';
    }, 'image/png');
  });

  cancelBtn.addEventListener('click', () => {
    modal.style.display = 'none';
    fileInput.value = '';
    if (cropper) { cropper.destroy(); cropper = null; }
    // Don’t revoke profilePicture.src here (user may still want to see it)
  });

  saveBtn.addEventListener('click', async () => {
    if (!croppedBlob) return;

    modal.style.display = 'none';
    if (cropper) { cropper.destroy(); cropper = null; }

    const formData = new FormData();
    formData.append('profile-picture', croppedBlob, 'cropped.png');

    const response = await sendAuthenticatedRequest(
      `${config.detectionUrl}/save-profile-pic`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData }
    );

    if (!response.ok) {
      alert('Error setting profile picture.');
    } else {
      // Optionally clear the blob after upload; keep preview visible
      // revoke(previewImageURL);  // keep if you prefer, but then set profilePicture to server copy
    }
  });

  // Cleanup on unload (avoid leaked blob URLs)
  window.addEventListener('beforeunload', () => {
    revoke(currentImageURL);
    revoke(previewImageURL);
    for (const u of Array.from(objectURLS)) revoke(u);
  });
}

async function handleAuthenticatedUser(user, token) {
  // Display the user's email
  document.getElementById('user-email').innerText = user.email;

  // Fetch the user's subscription status from your backend
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const usernameElement = document.getElementById('user-username');
  const setUsernameButton = document.getElementById('set-username-button');
  const cancelUsernameButton = document.getElementById(
    'cancel-username-button'
  );
  const usernameError = document.getElementById('username-error');
  const usernameInput = document.getElementById('username-input');
  const usernameForm = document.getElementById('username-form');

  const serverside_user = await getUser(headers); // Ensure this returns user data including 'username' and 'bio'

  setupProfilePic(token, serverside_user);

  // Fetch and update subscription status
  try {
    const response = await fetchCardData(
      `${config.detectionUrl}/user-status`,
      'GET',
      headers
    );
    const data = response.body;

    if (response.status === 200) {
      const hasLegacySubscription = ['collector', 'pro'].includes(
        data.subscription_level
      );
      document.getElementById('subscription-level').innerText = 'Full access';
      document.getElementById('upgrade-button').style.display = 'none';
      document.getElementById('manage-subscription-button').style.display =
        hasLegacySubscription ? 'inline-flex' : 'none';

      if (serverside_user.username && serverside_user.username.trim() !== '') {
        // User has a username
        usernameElement.innerText = serverside_user.username;
        setUsernameButton.style.display = 'none'; // Hide the Set Username button
        usernameForm.style.display = 'none'; // Ensure the form is hidden
      } else {
        // User does not have a username
        usernameElement.style.display = 'none';
        setUsernameButton.style.display = 'inline-flex'; // Show the Set Username button
        usernameForm.style.display = 'none'; // Initially hide the form
      }

      // **Username Handling Logic Ends Here**
    } else {
      console.error('Failed to fetch account status:', data.message);
      document.getElementById('subscription-level').innerText =
        'Error fetching data';
    }
  } catch (error) {
    console.error('Error:', error);
    document.getElementById('subscription-level').innerText =
      'Error fetching data';
  }

  // Handle Manage Subscription Button Click
  document
    .getElementById('manage-subscription-button')
    .addEventListener('click', async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };

        const response = await fetch(
          `${config.detectionUrl}/create-customer-portal-session`,
          {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({}), // No body required in this case
          }
        );

        const data = await response.json();

        if (response.ok) {
          // Redirect the user to the Stripe Customer Portal
          window.location.href = data.url;
        } else {
          console.error('Error creating customer portal session:', data.error);
          alert('An error occurred while redirecting to the customer portal.');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('An error occurred while redirecting to the customer portal.');
      }
    });

  // **Additional Username Functionality Starts Here**

  // Handle "Set Username" button click
  setUsernameButton.addEventListener('click', () => {
    usernameForm.style.display = 'flex'; // Show the form
    setUsernameButton.style.display = 'none'; // Hide the Set Username button
  });

  // Handle "Cancel" button click in the username form
  cancelUsernameButton.addEventListener('click', () => {
    usernameForm.style.display = 'none'; // Hide the form
    usernameError.style.display = 'none'; // Hide error messages
    if (serverside_user.username && serverside_user.username.trim() !== '') {
      usernameElement.style.display = 'inline';
      setUsernameButton.style.display = 'none';
    } else {
      setUsernameButton.style.display = 'inline-flex';
    }
    usernameInput.value = ''; // Clear input field
  });

  // Handle username form submission
  usernameForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const desiredUsername = usernameInput.value.trim();

    // Basic frontend validation
    const usernameRegex = /^[a-zA-Z0-9_]{3,15}$/;
    if (!usernameRegex.test(desiredUsername)) {
      usernameError.innerText =
        'Username must be 3-15 characters and contain only letters, numbers, and underscores.';
      usernameError.style.display = 'block';
      return;
    }

    // Attempt to set the username via your backend
    try {
      const payload = { username: desiredUsername };

      const response = await fetchCardData(
        `${config.detectionUrl}/set-username`,
        'POST',
        headers,
        payload
      );
      const data = response.body;

      if (data.username) {
        // Success: Update the UI
        usernameElement.innerText = desiredUsername;
        usernameElement.style.display = 'inline';
        usernameForm.style.display = 'none';
        usernameError.style.display = 'none';
        setUsernameButton.style.display = 'none';
        alert('Username set successfully!');
      } else {
        // Handle errors (e.g., username taken)
        usernameError.innerText = data.error || 'Failed to set username.';
        usernameError.style.display = 'block';
      }
    } catch (error) {
      console.error('Error setting username:', error);
      usernameError.innerText = 'An error occurred. Please try again.';
      usernameError.style.display = 'block';
    }
  });

  // **Additional Username Functionality Ends Here**

  // -------------------------------
  // New Bio Functionality Section
  // -------------------------------

  // Get the bio-related DOM elements
  const userBioElement = document.getElementById('user-bio');
  const setBioButton = document.getElementById('set-bio-button');
  const bioForm = document.getElementById('bio-form');
  const cancelBioButton = document.getElementById('cancel-bio-button');
  const bioInput = document.getElementById('bio-input');
  const bioError = document.getElementById('bio-error');

  // Set the current bio, or a default message if none exists
  if (serverside_user.bio && serverside_user.bio.trim() !== '') {
    userBioElement.innerText = serverside_user.bio;
  } else {
    userBioElement.innerText = 'No bio added yet.';
  }
  // Show the "Set Bio" button (if editing is allowed)
  setBioButton.style.display = 'inline-flex';

  // When the "Set Bio" button is clicked, hide the bio text and show the form
  setBioButton.addEventListener('click', () => {
    userBioElement.style.display = 'none';
    bioForm.style.display = 'flex'; // Or use "block" depending on your CSS layout
    // Pre-populate the textarea with the current bio if available
    bioInput.value = serverside_user.bio ? serverside_user.bio : '';
  });

  // When the Cancel button in the bio form is clicked, hide the form and show the bio text
  cancelBioButton.addEventListener('click', () => {
    bioForm.style.display = 'none';
    userBioElement.style.display = 'inline';
    bioError.style.display = 'none';
  });

  // Handle bio form submission
  bioForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const newBio = bioInput.value.trim();

    // Basic validation: bio must not be empty
    if (!newBio) {
      bioError.innerText = 'Bio cannot be empty.';
      bioError.style.display = 'block';
      return;
    }
    bioError.style.display = 'none';

    try {
      const response = await fetch(`${config.detectionUrl}/set-bio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bio: newBio }),
      });
      const updateData = await response.json();

      if (response.ok) {
        // Update the UI with the new bio
        userBioElement.innerText = newBio;
        bioForm.style.display = 'none';
        userBioElement.style.display = 'inline';
        // Optionally update serverside_user.bio if you need to reference it later
        serverside_user.bio = newBio;
      } else {
        bioError.innerText = updateData.error || 'Failed to update bio.';
        bioError.style.display = 'block';
      }
    } catch {
      bioError.innerText = 'An error occurred. Please try again.';
      bioError.style.display = 'block';
    }
  });
}

function handleUnauthenticatedUser() {
  // Redirect to the login page
  window.location.href = '/login.html';
}
